import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, Search, Trophy, Loader2 } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { useCategories } from '@/hooks/useCategories';
import { useUIStore } from '@/stores/uiStore';

export function MobileMenu() {
  const { t } = useTranslation();
  const { isMobileMenuOpen, closeMobileMenu } = useUIStore();
  const { data: categories, isLoading } = useCategories();
  const location = useLocation();

  return (
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeMobileMenu}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
          />

          {/* Menu panel */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-space-800 border-r border-cosmic-purple/20 lg:hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-cosmic-purple/20">
              <Logo />
              <button
                onClick={closeMobileMenu}
                className="p-2 hover:bg-space-700 rounded-lg transition-colors"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="p-4 space-y-2">
              <MobileNavLink
                to="/"
                icon={<Home className="w-5 h-5" />}
                onClick={closeMobileMenu}
                isActive={location.pathname === '/'}
              >
                {t('common.home')}
              </MobileNavLink>
              <MobileNavLink
                to="/search"
                icon={<Search className="w-5 h-5" />}
                onClick={closeMobileMenu}
                isActive={location.pathname === '/search'}
              >
                {t('common.search')}
              </MobileNavLink>
              <MobileNavLink
                to="/achievements"
                icon={<Trophy className="w-5 h-5" />}
                onClick={closeMobileMenu}
                isActive={location.pathname === '/achievements'}
              >
                {t('common.achievements')}
              </MobileNavLink>
            </nav>

            {/* Categories */}
            <div className="px-4 py-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                {t('common.categories')}
              </h3>

              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-cosmic-purple" />
                </div>
              )}

              <div className="space-y-1">
                {categories?.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.slug}`}
                    onClick={closeMobileMenu}
                    className={`
                      flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                      ${location.pathname === `/category/${category.slug}`
                        ? 'bg-cosmic-purple/20 text-white'
                        : 'text-gray-300 hover:bg-space-700'
                      }
                    `}
                  >
                    <span className="text-xl">{category.icon}</span>
                    <span>{category.name}</span>
                    <span className="ml-auto text-xs text-gray-500">
                      {category.animals_count}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface MobileNavLinkProps {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  isActive: boolean;
}

function MobileNavLink({ to, icon, children, onClick, isActive }: MobileNavLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
        ${isActive
          ? 'bg-cosmic-purple/20 text-white border border-cosmic-purple/40'
          : 'text-gray-300 hover:bg-space-700'
        }
      `}
    >
      {icon}
      <span className="font-medium">{children}</span>
    </Link>
  );
}
